import * as React from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const names = [
  'C',  // Carbon
  'H',  // Hydrogen
  'O',  // Oxygen
  'N',  // Nitrogen
  'P',  // Phosphorus
  'S',  // Sulfur
  'F',  // Fluorine
  'Cl', // Chlorine
  'Br', // Bromine
  'I',  // Iodine
  'Si', // Silicon
  'B',  // Boron
  'Se', // Selenium
  'As', // Arsenic
  'Na', // Sodium
  'K',  // Potassium
  'Mg', // Magnesium
  'Ca', // Calcium
  'Fe', // Iron
  'Zn', // Zinc
];

function getStyles(name: string, personName: readonly string[], theme: Theme) {
  return {
    fontWeight: personName.includes(name)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

type AtomsArrayProps = {
  title?: string;
  onChange?: (selectedAtoms: string[]) => void;
  readOnly?: boolean;
  exclude?: string[]; // New property to exclude certain atoms
  selected?: string[]; // New property to set preselected atoms
};

export default function MultipleSelectChip({ title, onChange, readOnly = false, exclude = [], selected = [] }: AtomsArrayProps) {
  const theme = useTheme();
  // Initialize state with selected atoms if provided
  const [personName, setPersonName] = React.useState<string[]>(selected);

  // Update state if selected prop changes
  React.useEffect(() => {
    if (selected) {
      setPersonName(selected);
    }
  }, [selected]);

  const handleChange = (event: SelectChangeEvent<typeof personName>) => {
    // If component is read-only, don't allow changes
    if (readOnly) return;
    
    const {
      target: { value },
    } = event;
    
    // Get the selected atoms
    const selectedAtoms = typeof value === 'string' ? value.split(',') : value;
    
    // Update local state
    setPersonName(selectedAtoms);
    
    // Call the onChange callback if provided
    if (onChange) {
      onChange(selectedAtoms);
    }
  };

  // Filter the names array to exclude atoms specified in the exclude prop
  const filteredNames = names.filter(name => !exclude.includes(name));

  return (
    <div>
      <FormControl sx={{ m: 1, width: '100%', minWidth: '200px' }}>
        <InputLabel id="demo-multiple-chip-label">{title || "Chip"}</InputLabel>
        <Select
          labelId="demo-multiple-chip-label"
          id="demo-multiple-chip"
          multiple
          value={personName}
          onChange={handleChange}
          input={<OutlinedInput id="select-multiple-chip" label={title || "Chip"} />}
          renderValue={(selected) => (
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 0.5,
              minHeight: '32px', // Ensure minimum height when empty
              alignItems: 'center'
            }}>
              {selected.map((value) => (
                <Chip key={value} label={value} />
              ))}
            </Box>
          )}
          MenuProps={MenuProps}
          disabled={readOnly}
          sx={{
            // Additional styling for the read-only state
            ...(readOnly && {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: 'rgba(0, 0, 0, 0.15)',
              },
            }),
            // Ensure consistent height
            '& .MuiSelect-select': {
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center'
            }
          }}
        >
          {filteredNames.map((name) => (
            <MenuItem
              key={name}
              value={name}
              style={getStyles(name, personName, theme)}
              disabled={readOnly} // Disable menu items when in read-only mode
            >
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}